import { useEffect } from 'react';

import { TRetailerTags } from '@lib/core/retailers/types/retailerDetail';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { localeWidget } from '@lib/tools/locale/source/web/widget';

import lockOpenedImage from '@components/web/src/assets/icons/eventsGame/lock_opened.png';
import * as S from '@components/web/src/atoms/Banner/VoucherBanner/styles';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS, RETAILER_COLORS } from '@components/web/src/styles';

interface Props {
  discountCoupon: TRetailerTags['discountCoupon'];
  setIsDiscountVoucherShown: (isShown: boolean) => void;
}

export const VoucherBanner = ({ discountCoupon, setIsDiscountVoucherShown }: Props) => {
  const { voucherText, discountUnlockedText } = localeWidget.discountBanner;
  const { code } = discountCoupon;

  const handleCopyDiscountId = async () => {
    if (!code) return;
    MixpanelTracker.events.discountCodeCopiedEvent({ discountCodePosition: 'Voucher', discountCodeText: code });
    await navigator.clipboard.writeText(code);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsDiscountVoucherShown(true);
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <S.VoucherBannerWrapper>
      <svg fill="none" height="162" style={{ minWidth: '70px' }} viewBox="0 0 70 162" width="70">
        <path
          d="M20.001 162H70.001V0H20.001C8.95529 0 0.000989914 8.95431 0.000989914 20L3.33779 20.7392C14.7631 23.2703 14.8587 39.5243 3.46404 42.1897L0 43V54L1.15158 54.2502C12.698 56.7592 12.7063 73.2256 1.16242 75.7462L0 76L0.000989914 87L1.30625 87.3039C12.8147 89.9838 12.5783 106.449 0.997678 108.798L0.000989914 109L0 120L1.44631 120.359C12.9158 123.209 12.4348 139.672 0.818519 141.847L0.000989914 142C0.000989914 153.046 8.95529 162 20.001 162Z"
          fill="url(#paint0_linear_39775_2813)"
        />
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id="paint0_linear_39775_2813"
            x1="-0.00109012"
            x2="117.974"
            y1="161.999"
            y2="111.021"
          >
            <stop stopColor="#3D80C1" />
            <stop offset="1" stopColor="#24A360" />
          </linearGradient>
        </defs>
      </svg>
      <svg className="line" fill="none" height="162" viewBox="0 0 2 162" width="2">
        <line stroke="#454C5A" strokeDasharray="8 8" strokeWidth="2" x1="1" x2="0.999993" y1="4.3711e-08" y2="162" />
      </svg>
      <Text
        color={COLORS.base.white}
        size="h6"
        style={{ left: '-14px', position: 'absolute', top: '50%', transform: 'translateY(-50%) rotate(-90deg)' }}
        text={voucherText}
        weight="medium"
      />
      <Flexbox
        direction="column"
        gap={18}
        padding="26px 22px"
        style={{ background: `${COLORS.base.white}`, width: '100%' }}
      >
        <Flexbox align="center" gap={14}>
          <Text color={RETAILER_COLORS.primary[700]} size="h5" text={discountUnlockedText} weight="bold" />
          <S.Icon alt="icon" src={lockOpenedImage} />
        </Flexbox>
        <Text
          color={COLORS.base.black}
          size="h6"
          style={{ cursor: 'copy' }}
          text={code}
          weight="medium"
          onClick={handleCopyDiscountId}
        />
      </Flexbox>
    </S.VoucherBannerWrapper>
  );
};
